// Mirrors backend decoder rules: phone XOR email, 10-digit phone, login email regex.
export const IDENTIFIER_TYPE = {
  PHONE: "phone",
  EMAIL: "email",
};

export const PHONE_REGEX = /^\d{10}$/;
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const OTP_LENGTH = 6;

export const validateIdentifier = (type, value) => {
  const v = (value || "").trim();
  if (type === IDENTIFIER_TYPE.PHONE) {
    return PHONE_REGEX.test(v)
      ? null
      : "Enter a valid 10-digit phone number";
  }
  if (type === IDENTIFIER_TYPE.EMAIL) {
    return EMAIL_REGEX.test(v) ? null : "Enter a valid email address";
  }
  return "Select phone or email";
};

// Backend wants the unused identifier ABSENT (not empty), so use phone XOR email.
export const buildIdentifierBody = (type, value, extra = {}) => {
  const v = (value || "").trim();
  const body =
    type === IDENTIFIER_TYPE.PHONE ? { phone_number: v } : { email: v };
  return { ...body, ...extra };
};

// Flow engine enums — mirror admin-ui's work_flow constants.
export const FLOW_REQUEST_TYPE = {
  LIST: "LIST",
  GET_WITH_VALUES: "GET_WITH_VALUES",
};

export const FLOW_UPDATE_TYPE = {
  SAVE_DRAFT: "SAVE_DRAFT",
  MARK_STEP_COMPLETE: "MARK_STEP_COMPLETE",
  COMPLETE_FLOW: "COMPLETE_FLOW",
  CANCEL_FLOW: "CANCEL_FLOW",
};

export const FLOW_STATUS = {
  CREATED: "CREATED",
  ASSIGNED: "ASSIGNED",
  INITIATED: "INITIATED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
};

// Seller onboarding request status — top-level field in GET response.
export const SOR_STATUS = {
  INITIATED: "SELLER_ONBOARDING_REQUEST_STATUS_INITIATED",
  IN_PROGRESS: "SELLER_ONBOARDING_REQUEST_STATUS_IN_PROGRESS",
  COMPLETED: "SELLER_ONBOARDING_REQUEST_STATUS_COMPLETED",
};

// Payment order status — top-level field in GET response.
export const PAYMENT_STATUS = {
  UNSPECIFIED: "ORDER_STATUS_UNSPECIFIED",
  INITIATED: "ORDER_STATUS_INITIATED",
  CANCELLED: "ORDER_STATUS_CANCELLED",
  COMPLETED: "ORDER_STATUS_COMPLETED",
};

export const ONBOARDING_EVENT = {
  WINDOW_FOCUS: "focus",
  APP_RESUME: "hp-app-resume",
  VISIBILITY_CHANGE: "visibilitychange",
};

export const DOCUMENT_VISIBILITY_STATE = {
  HIDDEN: "hidden",
  VISIBLE: "visible",
};

export const SUBSTEP_TYPE = {
  TEXT_INPUT: "TEXT_INPUT",
  IMAGE_INPUT: "IMAGE_INPUT",
  TODO_LIST: "TODO_LIST",
};

export const OWNERSHIP_TYPE_OPTIONS = [
  { label: "Proprietorship", value: "Proprietorship" },
  { label: "Individual", value: "Individual" },
  { label: "Private Limited Company", value: "Private Limited Company" },
  { label: "HUF", value: "HUF" },
  { label: "Limited Liability Partnership (LLP)", value: "Limited Liability Partnership (LLP)" },
  { label: "Public Limited Company", value: "Public Limited Company" },
  { label: "Others", value: "Others" },
];

export const CIN_REQUIRED_OWNERSHIP_TYPES = new Set([
  "Private Limited Company",
  "Limited Liability Partnership (LLP)",
  "Public Limited Company",
]);

export const isBlankOrNa = (value) => {
  const normalized = (value ?? "").toString().trim().toUpperCase();
  return normalized === "" || normalized === "NA";
};

export const needsCinForOwnership = (ownershipType) =>
  CIN_REQUIRED_OWNERSHIP_TYPES.has((ownershipType ?? "").toString().trim());

// Backend's document upload fileType is a closed enum unrelated to FlowConfig
// substep keys. Every onboarding document substep sends this same literal —
// substep_id (already in the flow record) carries the field's real identity,
// not the S3 path. Matches model.DOCUMENT_TYPE_SELLER_DOCUMENTS in admin-api.
export const ONBOARDING_DOCUMENT_FILE_TYPE = "seller_documents";

// Keys that are optional (not required by the frontend gate).
// isSubstepFilled returns true for these; the required * mark is also suppressed.
// contact_email / contact_phone: the secondary channel is optional because:
//   - user who signed up with phone can't verify email yet (second-channel OTP bug)
//   - user who signed up with email can't verify phone yet (same reason)
//   Both are excluded from the MARK_STEP_COMPLETE payload when empty (see toValuesArray).
export const OPTIONAL_KEYS = new Set([
  "contact_email",
  "contact_phone",
]);

// Keys whose values are auto-populated by the frontend (rendered read-only).
// These fields cannot be edited by the user — they are filled programmatically:
//   account_holder_name_input / bank_name_input / bank_address_input: filled after bank verify.
//   shipping_state: filled when shipping_city is selected.
// SubstepValue passes isReadOnly=true automatically for these keys.
export const AUTO_FILLED_KEYS = new Set([
  "account_holder_name_input",
  "bank_name_input",
  "bank_address_input",
  "shipping_state",
]);

// Detects the "disclosure radio with primitives" pattern in a TODO_LIST —
// items represent mutually exclusive Yes/No choices where each option reveals
// different form fields. Keyed on item keys or titles being Yes/No-style.
// Used by both OnboardingForm gate logic and TodoList rendering.
export const isDisclosureRadioItems = (items) => {
  if (!items || items.length < 2) return false;
  const allYesNo = items.every((it) => {
    const k = (it.key ?? "").toLowerCase();
    const t = (it.title ?? "").toLowerCase().trim();
    return (
      k.endsWith("_yes") || k.endsWith("_no") ||
      t === "yes" || t === "no"
    );
  });
  const hasPrimitives = items.some((it) => (it.primitive_inputs ?? []).length > 0);
  return allYesNo && hasPrimitives;
};

// User-facing copy that has no per-flow backend equivalent. Kept here so
// product/comms can edit one place instead of grep-and-replace across JSX.
// If FlowConfig grows a `completion_message` field one day, swap these for
// flow.flow_config.completion_message and delete these constants.
export const ONBOARDING_COPY = {
  loadErrorTitle: "Couldn’t load your application.",
  retry: "Retry",
  successTitle: "Application submitted",
  successBody:
    "Thank you. We’ll review your application and get back to you within 5–7 business days.",
  overviewSubtitleAllDone:
    "All steps complete. Submit your application when you’re ready.",
  overviewSubtitlePending:
    "Pick a step to continue, or open the first pending one below.",
  saveDraft: "Save",
  continue: "Continue",
  saveAndSubmit: "Save & Submit",
  previous: "← Previous",
  saving: "Saving…",
  saved: "Saved ✓",
  pleaseWait: "Please wait…",
};
